/**
 * AcaciaTree — SVG visual representation of a user's acacia plant.
 * Props:
 *   stage       {0|1|2|3|4}  growth stage
 *   islandId    {1|2|3|4|5}  determines leaf/pot color scheme
 *   healthState {'healthy'|'thirsty'|'yellowing'|'drying'|'dormant'}
 *   size        {number}  width/height in px (default 120)
 *   className   {string}
 */

const ISLAND_COLORS = {
  1: { leaves: "#C4B5FD", pot: "#7C3AED", trunk: "#8B6914" },
  2: { leaves: "#FCA5A5", pot: "#DC2626", trunk: "#8B6914" },
  3: { leaves: "#93C5FD", pot: "#2563EB", trunk: "#8B6914" },
  4: { leaves: "#FDE68A", pot: "#D97706", trunk: "#8B6914" },
  5: { leaves: "#86EFAC", pot: "#16A34A", trunk: "#8B6914" },
};

const HEALTH_FILTERS = {
  healthy: "",
  thirsty: "sepia(30%)",
  yellowing: "sepia(60%) hue-rotate(-20deg)",
  drying: "grayscale(40%) opacity(0.7)",
  dormant: "grayscale(70%) opacity(0.5)",
};

const FLOWER_COLOR = "#FBBF24";
const SOIL_COLOR = "#92400E";
const DIRT_COLOR = "#B45309";

export default function AcaciaTree({
  stage = 0,
  islandId = 1,
  healthState = "healthy",
  size = 120,
  className = "",
}) {
  const colors = ISLAND_COLORS[islandId] || ISLAND_COLORS[1];
  const filter = HEALTH_FILTERS[healthState] || "";

  const vb = "0 0 100 130";

  return (
    <svg
      viewBox={vb}
      width={size}
      height={size * 1.3}
      className={className}
      style={{ filter: filter || undefined }}
      aria-label={`Acácia estágio ${stage}`}
    >
      {/* Pot */}
      <PotShape color={colors.pot} />

      {/* Soil */}
      {stage >= 1 && <Soil />}

      {/* Plant by stage */}
      {stage === 0 && <EmptyPot />}
      {stage === 1 && <Seedling trunkColor={colors.trunk} />}
      {stage === 2 && <SaplingWithLeaves trunkColor={colors.trunk} leafColor={colors.leaves} />}
      {stage === 3 && <YoungTree trunkColor={colors.trunk} leafColor={colors.leaves} />}
      {stage === 4 && <FullTree trunkColor={colors.trunk} leafColor={colors.leaves} />}
    </svg>
  );
}

/* ── Sub-components ─────────────────────────────────────────────── */

function PotShape({ color }) {
  // Trapezoid pot
  return (
    <g>
      {/* Pot body */}
      <polygon
        points="28,90 72,90 80,125 20,125"
        fill={color}
      />
      {/* Pot rim */}
      <rect x="22" y="85" width="56" height="8" rx="3" fill={color} />
      {/* Pot highlight */}
      <polygon
        points="30,90 40,90 35,125 25,125"
        fill="white"
        opacity="0.12"
      />
    </g>
  );
}

function Soil() {
  return (
    <ellipse cx="50" cy="88" rx="26" ry="5" fill={SOIL_COLOR} />
  );
}

function EmptyPot() {
  // Just soil + a tiny sprout dot
  return (
    <ellipse cx="50" cy="86" rx="18" ry="4" fill={DIRT_COLOR} opacity="0.7" />
  );
}

function Seedling({ trunkColor }) {
  return (
    <g>
      {/* Thin trunk */}
      <line x1="50" y1="88" x2="50" y2="68" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      {/* Two tiny leaves */}
      <ellipse cx="44" cy="66" rx="5" ry="3" fill="#86EFAC" transform="rotate(-30,44,66)" />
      <ellipse cx="56" cy="66" rx="5" ry="3" fill="#86EFAC" transform="rotate(30,56,66)" />
    </g>
  );
}

function SaplingWithLeaves({ trunkColor, leafColor }) {
  return (
    <g>
      {/* Trunk */}
      <line x1="50" y1="88" x2="50" y2="55" stroke={trunkColor} strokeWidth="4" strokeLinecap="round" />
      {/* Branch left */}
      <line x1="50" y1="70" x2="36" y2="62" stroke={trunkColor} strokeWidth="2.5" strokeLinecap="round" />
      {/* Branch right */}
      <line x1="50" y1="68" x2="64" y2="60" stroke={trunkColor} strokeWidth="2.5" strokeLinecap="round" />
      {/* Leaf clusters */}
      <circle cx="50" cy="50" r="12" fill={leafColor} opacity="0.9" />
      <circle cx="36" cy="58" r="9" fill={leafColor} opacity="0.85" />
      <circle cx="64" cy="56" r="9" fill={leafColor} opacity="0.85" />
    </g>
  );
}

function YoungTree({ trunkColor, leafColor }) {
  return (
    <g>
      {/* Trunk */}
      <line x1="50" y1="88" x2="50" y2="42" stroke={trunkColor} strokeWidth="5" strokeLinecap="round" />
      {/* Branches */}
      <line x1="50" y1="75" x2="32" y2="65" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      <line x1="50" y1="68" x2="68" y2="58" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      <line x1="50" y1="58" x2="34" y2="48" stroke={trunkColor} strokeWidth="2.5" strokeLinecap="round" />
      <line x1="50" y1="55" x2="66" y2="46" stroke={trunkColor} strokeWidth="2.5" strokeLinecap="round" />
      {/* Canopy */}
      <circle cx="50" cy="36" r="16" fill={leafColor} opacity="0.9" />
      <circle cx="34" cy="44" r="12" fill={leafColor} opacity="0.85" />
      <circle cx="66" cy="43" r="12" fill={leafColor} opacity="0.85" />
      <circle cx="36" cy="34" r="10" fill={leafColor} opacity="0.8" />
      <circle cx="64" cy="33" r="10" fill={leafColor} opacity="0.8" />
    </g>
  );
}

function FullTree({ trunkColor, leafColor }) {
  return (
    <g>
      {/* Trunk — thicker */}
      <rect x="46" y="62" width="8" height="28" rx="4" fill={trunkColor} />
      {/* Main branches */}
      <line x1="50" y1="80" x2="28" y2="68" stroke={trunkColor} strokeWidth="4" strokeLinecap="round" />
      <line x1="50" y1="76" x2="72" y2="65" stroke={trunkColor} strokeWidth="4" strokeLinecap="round" />
      <line x1="50" y1="70" x2="30" y2="55" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      <line x1="50" y1="67" x2="70" y2="52" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      <line x1="50" y1="62" x2="50" y2="42" stroke={trunkColor} strokeWidth="3" strokeLinecap="round" />
      {/* Dense canopy */}
      <circle cx="50" cy="30" r="20" fill={leafColor} opacity="0.92" />
      <circle cx="30" cy="42" r="15" fill={leafColor} opacity="0.88" />
      <circle cx="70" cy="40" r="15" fill={leafColor} opacity="0.88" />
      <circle cx="32" cy="28" r="13" fill={leafColor} opacity="0.85" />
      <circle cx="68" cy="27" r="13" fill={leafColor} opacity="0.85" />
      <circle cx="50" cy="20" r="14" fill={leafColor} opacity="0.9" />
      {/* Flowers — stage 4 gets yellow blooms */}
      <Flowers />
    </g>
  );
}

function Flowers() {
  const positions = [
    [42, 18], [58, 16], [30, 30], [70, 28], [50, 12], [38, 34], [62, 32],
  ];
  return (
    <g>
      {positions.map(([cx, cy], i) => (
        <g key={i}>
          <circle cx={cx} cy={cy} r={3.5} fill={FLOWER_COLOR} opacity="0.95" />
          <circle cx={cx} cy={cy} r={1.5} fill="white" opacity="0.8" />
        </g>
      ))}
    </g>
  );
}
